import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  DoughnutController,
} from 'chart.js';
import { format, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  FaClipboardList,
  FaChartLine,
  FaUsers,
  FaCalendarAlt,
} from 'react-icons/fa';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  DoughnutController
);

const Home = () => {
  const [formStats, setFormStats] = useState([]);
  const [dailyStats, setDailyStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedForm, setSelectedForm] = useState('all');
  const [totalResponses, setTotalResponses] = useState(0);
  const [totalForms, setTotalForms] = useState(0);

  const [dateRange, setDateRange] = useState({
    startDate: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchFormStats(),
          fetchDailyReports(dateRange.startDate, dateRange.endDate),
        ]);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateRange]);

  const fetchFormStats = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(
      process.env.REACT_APP_URL_BASE_BACKEND + '/api/forms/stats/responses',
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Error al obtener estadísticas');
    }

    const data = await response.json();
    setFormStats(data);

    // Calcular estadísticas totales
    const total = data.reduce((sum, form) => sum + form.responseCount, 0);
    setTotalResponses(total);
    setTotalForms(data.length);
  };

  const fetchDailyReports = async (startDate, endDate) => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ startDate, endDate });

    const response = await fetch(
      process.env.REACT_APP_URL_BASE_BACKEND +
        `/api/forms/reports/daily?${params}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Error al obtener reportes diarios');
    }

    const data = await response.json();
    setDailyStats(data);
  };

  // Modify chartData to handle multiple datasets
  const chartData = {
    labels: dailyStats
      ? Object.keys(dailyStats.data).map(date => {
          const [year, month, day] = date.split('-');
          return `${day}/${month}`;
        })
      : [],
    datasets: dailyStats
      ? [
          {
            label: 'Total Respuestas por Día',
            data: Object.keys(dailyStats.data).map(date => {
              if (selectedForm === 'all') {
                return dailyStats.data[date].reduce(
                  (sum, form) => sum + form.responseCount,
                  0
                );
              }
              const formData = dailyStats.data[date].find(
                form => form.formId === selectedForm
              );
              return formData ? formData.responseCount : 0;
            }),
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.5)',
            tension: 0.3,
            fill: true,
          },
        ]
      : [],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Respuestas Diarias',
        font: {
          size: 16,
          weight: 'bold',
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };
  // Add form selector component
  const FormSelector = () => (
    <div className="mb-4">
      <label className="block text-sm font-medium mb-1">
        Seleccionar Formulario
      </label>
      <select
        value={selectedForm}
        onChange={e => setSelectedForm(e.target.value)}
        className="border rounded p-2 w-full md:w-auto focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="all">Todos los formularios</option>
        {formStats.map(form => (
          <option key={form._id} value={form._id}>
            {form.formName.replace(/<[^>]*>/g, '')}
          </option>
        ))}
      </select>
    </div>
  );

  // Calcular tendencia: comparar último día con el día anterior
  const calculateTrend = () => {
    if (!dailyStats || !Object.keys(dailyStats.data).length) return 0;

    const dates = Object.keys(dailyStats.data).sort();
    if (dates.length < 2) return 0;

    const lastDay = dates[dates.length - 1];
    const prevDay = dates[dates.length - 2];

    const lastDayCount = dailyStats.data[lastDay].reduce(
      (sum, form) => sum + form.responseCount,
      0
    );
    const prevDayCount = dailyStats.data[prevDay].reduce(
      (sum, form) => sum + form.responseCount,
      0
    );

    if (prevDayCount === 0) return 100;
    return Math.round(((lastDayCount - prevDayCount) / prevDayCount) * 100);
  };

  const trend = calculateTrend();

  // Calcular el total de respuestas en el rango de fecha seleccionado
  const calculatePeriodTotal = () => {
    if (!dailyStats) return 0;

    return Object.keys(dailyStats.data).reduce((total, date) => {
      return (
        total +
        dailyStats.data[date].reduce((sum, form) => sum + form.responseCount, 0)
      );
    }, 0);
  };

  const periodTotal = calculatePeriodTotal();

  // Obtener el formulario más popular
  const getMostPopularForm = () => {
    if (!formStats.length) return { name: 'Ninguno', count: 0 };
    const sorted = [...formStats].sort(
      (a, b) => b.responseCount - a.responseCount
    );
    return {
      name: sorted[0].formName.replace(/<[^>]*>/g, ''),
      count: sorted[0].responseCount,
    };
  };

  const mostPopular = getMostPopularForm();

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-6 text-base-content">
        Panel Principal
      </h1>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : error ? (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          <p>{error}</p>
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-base-100 rounded-lg shadow-md p-5 border-l-4 border-blue-500 transition-transform hover:scale-105">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Total Respuestas</p>
                  <p className="text-2xl font-bold">{totalResponses}</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-full">
                  <FaClipboardList className="text-blue-500 text-xl" />
                </div>
              </div>
            </div>

            <div className="bg-base-100 rounded-lg shadow-md p-5 border-l-4 border-green-500 transition-transform hover:scale-105">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">
                    Respuestas Periodo
                  </p>
                  <p className="text-2xl font-bold">{periodTotal}</p>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <FaCalendarAlt className="text-green-500 text-xl" />
                </div>
              </div>
            </div>

            <div className="bg-base-100 rounded-lg shadow-md p-5 border-l-4 border-purple-500 transition-transform hover:scale-105">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">
                    Total Formularios
                  </p>
                  <p className="text-2xl font-bold">{totalForms}</p>
                </div>
                <div className="bg-purple-100 p-3 rounded-full">
                  <FaUsers className="text-purple-500 text-xl" />
                </div>
              </div>
            </div>

            <div className="bg-base-100 rounded-lg shadow-md p-5 border-l-4 border-amber-500 transition-transform hover:scale-105">
              <div className="flex justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Tendencia</p>
                  <p className="text-2xl font-bold flex items-center">
                    {trend}%
                    {trend > 0 && (
                      <span className="text-green-500 ml-1">↑</span>
                    )}
                    {trend < 0 && <span className="text-red-500 ml-1">↓</span>}
                    {trend === 0 && (
                      <span className="text-gray-500 ml-1">→</span>
                    )}
                  </p>
                </div>
                <div className="bg-amber-100 p-3 rounded-full">
                  <FaChartLine className="text-amber-500 text-xl" />
                </div>
              </div>
            </div>
          </div>

          {/* Controls Section */}
          <div className="mb-6 bg-base-100 p-6 rounded-lg shadow-md border border-base-200">
            <h2 className="text-xl text-base-content font-semibold mb-4 border-b pb-2">
              Filtros de Análisis
            </h2>
            <div className="space-y-4">
              <FormSelector />
              <div className="flex flex-wrap gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Fecha Inicio
                  </label>
                  <input
                    type="date"
                    value={dateRange.startDate}
                    onChange={e =>
                      setDateRange(prev => ({
                        ...prev,
                        startDate: e.target.value,
                      }))
                    }
                    className="border rounded p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Fecha Fin
                  </label>
                  <input
                    type="date"
                    value={dateRange.endDate}
                    onChange={e =>
                      setDateRange(prev => ({
                        ...prev,
                        endDate: e.target.value,
                      }))
                    }
                    className="border rounded p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Daily Stats Chart */}
            {dailyStats && (
              <div className="bg-base-100 p-6 rounded-lg shadow-md border border-base-200 lg:col-span-2">
                <h3 className="text-lg text-base-content font-semibold mb-4 border-b pb-2">
                  Respuestas por Día
                </h3>
                <div className="h-72">
                  <Line data={chartData} options={chartOptions} />
                </div>
              </div>
            )}

            {/* Total Stats Summary */}
            <div className="bg-base-100 p-6 rounded-lg shadow-md border border-base-200">
              <h3 className="text-lg text-base-content font-semibold mb-4 border-b pb-2">
                Resumen de Formularios
              </h3>
              {formStats.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-gray-500">
                  <p>No hay datos disponibles</p>
                </div>
              ) : (
                <div>
                  <div className="mb-4 bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-blue-700">
                      Formulario más popular:
                    </h4>
                    <p className="font-bold mt-1">{mostPopular.name}</p>
                    <p className="text-sm text-blue-600 mt-1">
                      {mostPopular.count} respuestas
                    </p>
                  </div>

                  <div className="space-y-3 max-h-56 overflow-y-auto pr-2">
                    {formStats.map(form => (
                      <div
                        key={form._id}
                        className="border-b pb-3 last:border-0 hover:bg-gray-50 p-2 rounded transition-colors"
                      >
                        <div className="flex justify-between items-center">
                          <h4
                            className="font-medium text-base-content truncate flex-1"
                            title={form.formName.replace(/<[^>]*>/g, '')}
                          >
                            {form.formName.replace(/<[^>]*>/g, '').length > 24
                              ? form.formName
                                  .replace(/<[^>]*>/g, '')
                                  .substring(0, 24) + '...'
                              : form.formName.replace(/<[^>]*>/g, '')}
                          </h4>
                          <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                            {form.responseCount}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4">
                    <Link
                      to="/myforms"
                      className="text-blue-600 hover:text-blue-800 transition-colors font-medium text-sm inline-flex items-center"
                    >
                      Ver todos los formularios
                      <svg
                        className="w-3 h-3 ml-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        ></path>
                      </svg>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Home;
